import { buildProductArgs } from 'Utils/graphql';

function productFeatureServiceQuery(lineProps) {
  const { ageInformation, groupMin, groupMax, groupRange } = lineProps;
  return `
    hasBookingOnline
    hasBookingOnsite
    id
    isIdealForFamily
    label
    description
    detailedDescription
    durationDescription
    ageRestriction(message: "${ageInformation}")
    level
    note
    group(min: "${groupMin}" max: "${groupMax}" range: "${groupRange}")
    specificConditions
    equipments {
      label
      included
    }
    utilities {
      label
      included
    }
    targetPrice {
      price
      currency
    }
  `;
}

const productFeatureFacilityQuery = `
    description
    distance
    id
    label
    localisation
    nightLighting
    onSite
    outdoor
    quantity
    specificInformation {
      type
      value
    }
    technicalSpecificities {
      typeDescription
      length
      width
      minDepth
      maxDepth
      unit
    }
  `;

export function productChildcareDetailQuery(productId, activityId, { lineProps, urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  const productActivitiesArgs = `activityId : "${activityId}"`;
  const activityArgs = `categoryId: "${activityId}"`;
  const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';

  return `query {
    config {
      common { previous next }
    }
    resort(${productArgs}) {
      facilities(types: ["CHILDREN"], withEquipments: true) {
        subtitle
        label
        equipments {
          id
          label
        }
      }
      productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
        subtitle
        label
        equipments {
          id
          label
        }
      }
    }
    productActivities(${productArgs}) {
      childcare(${productActivitiesArgs}) {
        closingWeekdays
        description
        duration
        furtherInformation
        id
        label
        openingDates {
          opening
          closing
        }
        openingHours {
          start
          end
        }
        timeSlots {
          type
          opening
          closing
        }
        philosophy
        targetPrice {
          currency
          price
        }
      }
      facilities(${productActivitiesArgs}) {
        ${productFeatureFacilityQuery}
      }
    }
    includedServices: activities(${productArgs}, ${activityArgs}, onlyIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
    }
    onDemandServices: activities(${productArgs}, ${activityArgs}, onlyNotIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
    }
  }`;
}

/* lists of features */
const productFeaturesConfigQuery = `
    config {
      common {
        allInclusive
        backNavigation
        bookOnlineOnly
        bookOnlineOrOnsite
        bookOnsiteOnly
        childcarealacarte
        downloadLeafletLabel
        extraCost
        howToBookLabel
        idealForFamilyLabel
        included
        loadLess
        onDemandService
        philosophyLabel
        price {
          priceFrom
        }
        scrollBackward
        scrollForward
        treatmentsLabel
        viewMore
      }
    }
  `;

function productFeaturesCategoriesFields(defaultLabel) {
  const activityLabel = defaultLabel ? `label(defaultLabel: "${defaultLabel}")` : 'label';

  return `
    activities: subCategories {
      bookingDescription
      bookletUrl
      brandLogoUrl
      description
      equipmentRentalDescription
      id
      image: imageUrl
      isChildrenClub
      isIncluded
      keywords
      ${activityLabel}
      name
      numberOfIncludedActivities
      numberOfOnDemandActivities
      philosophy
      ranking
      rentalEquipmentsUsecasesId
      supervisionDescription
      termsAndConditionsDescription
    }
    id
    label
    ranking
  `;
}

export function productChildcaresQuery(productId, { urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  return `query {
    ${productFeaturesConfigQuery}
    resort(${productArgs}) {
      kidsActivitiesIntroduction {
        description
        shortDescription
      }
      categories: activityCategories {
        children {
          ${productFeaturesCategoriesFields()}
        }
      }
    }
  }`;
}

/* ACTIVITIES */
export const activityCategoryFragment = `fragment activityCategory on UpsellsActivityCategoryType {
  activitiesCounters {
    included
    onDemand
  }
  bookingDescription
  bookletUrl
  description
  equipmentRentalDescription
  facilities {
    areaId
    categoriesIds
    description
    distanceAndTransferDescription
    equipments {
      id
      label
    }
    hasFreeAccess
    hasNightLighting
    id
    isOnSite
    isOutdoor
    level
    localisation
    name
    quantity
    ranking
    specificInformation {
      type
      value
    }
    subTitle
    technicalSpecificities {
      height
      isHeated
      length
      maxDepth
      minDepth
      typesDescription
      unit
      width
    }
  }
  id
  images
  keywords
  name
  philosophy
  ranking
  shortDescription
  supervisionDescription
  termsAndConditionsDescription
  upsellType
}`;

export const activityFragment = `fragment activity on UpsellsActivityType {
  ageRestriction {
    formatted
    min
    max
    specificConditions
  }
  categoriesIds
  description
  duration
  equipments {
    id
    isIncluded
    label
  }
  furtherInformation
  hasBookingOnline
  hasBookingOnsite
  id
  images
  isIncluded
  level
  name
  openingDates {
    endDate
    startDate
  }
  participants {
    min
    max
  }
  price {
    currency
    formattedValue
    value
  }
  ranking
  seasonality
  services {
    id
    isIncluded
    label
  }
  subtitle
  transportation
  treatments
  upsellType
}`;

export const thematicFragment = `fragment thematics on UpsellsActivityThematicType {
  activitiesCounters {
    included
    onDemand
  }
  description
  id
  images
  name
}
`;

export const rentalsFragment = `fragment rentals on RentalsTreeType {
  id
  label
  items {
    id
    label
    items {
      ageRestriction {
        formatted
        max
        min
      }
      availableBrands
      furtherInformation
      formalities
      id
      images
      levels {
        id
        label
      }
      longDescription
      name
      price {
        currency
        formattedValue
        value
      }
      productRange {
        id
        label
      }
      shortDescription
      sizes {
        max
        min
        unit
      }
      type {
        id
        label
      }
      upsellType
      usecase {
        id
        label
      }
    }
  }
}`;

export const productActivitiesQuery = ({ productId, urlParams }) => {
  const productArgs = buildProductArgs(productId, urlParams);
  return `query {
    resort(${productArgs}) {
      activityTree {
        ...activityCategory
        activities {
          ...activity
        }
        categories {
          ...activityCategory
          activities {
            ...activity
          }
          categories {
            ...activityCategory
            activities {
              ...activity
            }
          }
        }
      }
      thematicActivityTree {
        ...thematics
        activities {
          ...activity
        }
      }
      id
      name
      rentalsTree {
        ...rentals
      }
    }
  }
  ${activityCategoryFragment}
  ${activityFragment}
  ${rentalsFragment}
  ${thematicFragment}
  `;
};
